/**
 * Build-time environment validation.
 *
 * Runs at module import (before Sentry/Pinia/router boot) so missing
 * config fails loudly rather than producing a silently broken app.
 *
 * Why build-time: Vite bakes import.meta.env values into the bundle
 * during `pnpm build`. If a required value is missing or a TODO
 * placeholder, the bundle itself is broken — we want to catch that
 * during the build pipeline, not after users hit a white screen.
 */

type EnvMode = 'development' | 'preprod' | 'production' | 'test'

interface HudEnv {
  API_BASE_URL: string
  PAPERSTORE_URL: string
  SENTRY_DSN: string | undefined
  MODE: EnvMode
}

class EnvError extends Error {
  constructor(message: string) {
    super(`[env] ${message}`)
    this.name = 'EnvError'
  }
}

/**
 * Read a VITE_-prefixed env var. Returns undefined if absent or empty.
 * Placeholder detection: treat strings containing "TODO" / "XXX" /
 * "changeme" as if unset — they indicate an uninitialized deploy.
 */
function readEnv(key: string): string | undefined {
  const raw = import.meta.env[key] as string | undefined
  if (raw === undefined) return undefined
  // Trim accidental leading/trailing whitespace from dashboard paste-typos —
  // a single leading space turned a valid URL into a regex-validation failure
  // and blocked a deploy. Cheap defense.
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  if (/TODO|XXX|changeme/i.test(trimmed)) return undefined
  return trimmed
}

// ─── Policy ───────────────────────────────────────────────────────────
//
// Severity tiers:
//   CORE     — app cannot function. Fail in every mode (dev, test, prod).
//   PROD     — app functions but critical observability/features missing.
//              Fail only when MODE === 'production'. Warn elsewhere.
//   SHAPE    — if present, must look like a URL. Catches typos like missing
//              https:// or bare hostnames.
//
// Rationale:
//   - VITE_API_BASE_URL is CORE: every page talks to the API.
//   - VITE_PAPERSTORE_URL is PROD: PDF manifests are essential to the
//     delivery workflow; preprod/prod without it is a degraded product,
//     but dev without it just loses one feature tab.
//   - VITE_SENTRY_DSN is PROD: matches .env.example's "optional in dev"
//     comment. Shipping prod without error tracking is not negotiable —
//     we'd be blind to outages.
//   - Presence-only for DSN (no regex on the DSN format itself): the
//     DSN schema varies by Sentry version and over-validating creates
//     false positives during upgrades.
// ──────────────────────────────────────────────────────────────────────

// Accept absolute http(s) URLs (prod/preprod) AND root-relative paths
// (dev via Vite proxy). The relative form (`/api/v1`) is deliberate in dev
// to keep all traffic same-origin so SameSite=Lax cookies (refresh_token)
// ride the silent-refresh iframe — using an absolute cross-origin URL there
// silently blocks the cookie and ends sessions at the 15-min mark.
function looksLikeUrl(value: string): boolean {
  return /^https?:\/\/\S+$/.test(value) || /^\/\S*$/.test(value)
}

export function validateEnv(): HudEnv {
  const mode = (import.meta.env.MODE ?? 'development') as EnvMode
  const apiBaseUrl = readEnv('VITE_API_BASE_URL')
  const paperstoreUrl = readEnv('VITE_PAPERSTORE_URL')
  const sentryDsn = readEnv('VITE_SENTRY_DSN')
  // Treat preprod as a deployed tier: it serves real users (QA) over the
  // network, so missing PDF/Sentry config is as fatal there as in production —
  // not a dev-only warning. Only true `development`/`test` get the lax path.
  const isProd = mode === 'production' || mode === 'preprod'

  // Collect every violation before throwing so builders see the full list
  // in one run rather than fix-one-rebuild-find-next whack-a-mole.
  const errors: string[] = []
  const warnings: string[] = []

  // CORE
  if (!apiBaseUrl) {
    errors.push('VITE_API_BASE_URL is required (app cannot reach the backend without it).')
  } else if (!looksLikeUrl(apiBaseUrl)) {
    errors.push(`VITE_API_BASE_URL must be an http(s) URL, got: ${apiBaseUrl}`)
  }

  // PROD
  if (!paperstoreUrl) {
    const msg = 'VITE_PAPERSTORE_URL is missing — PDF generation will be broken.'
    if (isProd) {
      errors.push(msg)
    } else {
      warnings.push(msg)
    }
  } else if (!looksLikeUrl(paperstoreUrl)) {
    errors.push(`VITE_PAPERSTORE_URL must be an http(s) URL, got: ${paperstoreUrl}`)
  }

  if (!sentryDsn) {
    const msg = 'VITE_SENTRY_DSN is missing — error tracking disabled.'
    if (isProd) {
      errors.push(msg)
    } else {
      warnings.push(msg)
    }
  }

  if (warnings.length > 0) {
    console.warn(
      `[env] ${warnings.length} warning(s) in ${mode} mode:\n  - ${warnings.join('\n  - ')}`,
    )
  }
  if (errors.length > 0) {
    throw new EnvError(
      `${errors.length} fatal config error(s) in ${mode} mode:\n  - ${errors.join('\n  - ')}`,
    )
  }

  return {
    API_BASE_URL: apiBaseUrl!,
    PAPERSTORE_URL: paperstoreUrl ?? '',
    SENTRY_DSN: sentryDsn,
    MODE: mode,
  }
}

export const env = validateEnv()
